import { Vertex2 } from '@rta/rta-blueprint-library';

import { BREAKPOINT_SPACING } from './constants';
import { Vector } from '@r-core/types';

type YRange = { minY: number; maxY: number };

// Minimum segment height to create a closing vector (skip tiny slivers)
const MIN_SEGMENT_HEIGHT = 0.5;

// Check if a segment (fromY to toY) overlaps with any exclude range
const segmentOverlapsExcludeRange = (fromY: number, toY: number, excludeRanges: Array<YRange>): boolean => {
	const segMinY = Math.min(fromY, toY);
	const segMaxY = Math.max(fromY, toY);

	for (const range of excludeRanges) {
		// Check if segment is fully contained within the exclude range
		if (segMinY >= range.minY && segMaxY <= range.maxY) {
			return true;
		}
	}
	return false;
};

// Check if segment is too small to bother creating
const segmentTooSmall = (fromY: number, toY: number): boolean => {
	return Math.abs(toY - fromY) < MIN_SEGMENT_HEIGHT;
};

export const createClosingVectors = (
	splitX: number,
	leftYPositions: Array<number>,
	rightYPositions: Array<number>,
	leftExcludeRanges: Array<YRange>,
	rightExcludeRanges: Array<YRange>,
	referenceVector: Vector<Vertex2> | undefined
): Array<Vector<Vertex2>> => {
	const layer = referenceVector?.layer ?? 'Panel';
	const metadata = referenceVector?.metadata ?? {};
	const closingVectors: Array<Vector<Vertex2>> = [];

	// Create left closing vector segments (at splitX)
	// Skip segments that overlap with panelItem edges or are too small (slivers)
	if (leftYPositions.length >= 2) {
		const sortedLeftY = [...new Set(leftYPositions)].sort((a, b) => a - b);
		for (let i = 0; i < sortedLeftY.length - 1; i++) {
			const fromY = sortedLeftY[i];
			const toY = sortedLeftY[i + 1];

			// Skip if segment overlaps exclude range or is too small
			if (!segmentOverlapsExcludeRange(fromY, toY, leftExcludeRanges) && !segmentTooSmall(fromY, toY)) {
				closingVectors.push({
					from: new Vertex2({ dataIn: { x: splitX, y: fromY }}),
					to: new Vertex2({ dataIn: { x: splitX, y: toY }}),
					layer,
					angle: 0,
					metadata
				});
			}
		}
	}

	// Create right closing vector segments (at splitX + spacing)
	// Skip segments that overlap with panelItem edges or are too small (slivers)
	if (rightYPositions.length >= 2) {
		const sortedRightY = [...new Set(rightYPositions)].sort((a, b) => a - b);
		for (let i = 0; i < sortedRightY.length - 1; i++) {
			const fromY = sortedRightY[i];
			const toY = sortedRightY[i + 1];

			// Skip if segment overlaps exclude range or is too small
			if (!segmentOverlapsExcludeRange(fromY, toY, rightExcludeRanges) && !segmentTooSmall(fromY, toY)) {
				closingVectors.push({
					from: new Vertex2({ dataIn: { x: splitX + BREAKPOINT_SPACING, y: fromY }}),
					to: new Vertex2({ dataIn: { x: splitX + BREAKPOINT_SPACING, y: toY }}),
					layer,
					angle: 0,
					metadata
				});
			}
		}
	}

	return closingVectors;
};
